/**
 * Extract user-friendly error message from backend function errors.
 *
 * supabase-js wraps non-2xx responses as FunctionsHttpError:
 * - error.message: usually generic ("Edge Function returned a non-2xx status code")
 * - error.context: a Response-like object (may not be `instanceof Response` depending on fetch impl)
 *
 * Sometimes the JSON error is embedded in error.message (often with escaped quotes).
 */
export function parseEdgeFunctionError(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "Wystąpił nieoczekiwany błąd.";
  }

  const err = error as Record<string, unknown>;
  const message = typeof err.message === "string" ? err.message : "";

  // 1) Try to extract JSON from error.message (raw or escaped)
  const embedded = extractJsonObjectFromString(message);
  if (embedded) {
    const parsed = tryParseJsonObjectPossiblyEscaped(embedded);
    if (parsed?.error) return String(parsed.error);
  }

  // 2) Strip SDK prefix and clean up
  const stripped = message
    .replace(/^Edge function returned \d+:\s*/i, "")
    .replace(/^Error,\s*/i, "")
    .trim();

  return stripped || "Wystąpił błąd podczas wysyłania.";
}

/**
 * Async version that can read JSON from FunctionsHttpError.context (Response-like).
 */
export async function parseEdgeFunctionErrorAsync(error: unknown): Promise<string> {
  const base = parseEdgeFunctionError(error);

  if (!error || typeof error !== "object") return base;
  const err = error as Record<string, unknown>;

  const ctx = err.context as unknown;
  const responseLike = isResponseLike(ctx);
  if (responseLike) {
    try {
      // Prefer clone() when available to avoid consuming the body
      const res: any = ctx;
      const text = typeof res.clone === "function" ? await res.clone().text() : await res.text();
      const parsed = tryParseJsonObjectPossiblyEscaped(text);
      if (parsed?.error) return String(parsed.error);
    } catch {
      // ignore
    }
  }

  return base;
}

function isResponseLike(v: unknown): v is { text: () => Promise<string>; clone?: () => any } {
  if (!v || typeof v !== "object") return false;
  const anyV = v as any;
  return typeof anyV.text === "function";
}

function extractJsonObjectFromString(raw: string): string | null {
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) return null;
  return raw.slice(start, end + 1);
}

function tryParseJsonObjectPossiblyEscaped(str: string): Record<string, unknown> | null {
  // First try as-is
  const direct = tryParseJson(str);
  if (direct) return direct;

  // If quotes are escaped (e.g. {\"error\":\"...\"}) try unescaping
  const unescapedQuotes = str.replace(/\\"/g, '"');
  const second = tryParseJson(unescapedQuotes);
  if (second) return second;

  return null;
}

function tryParseJson(str: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(str);
    if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

